import { AvailableIcon } from "client/Api/AppIcon/appIcon";
import {
	generateTrackingId,
	isNil,
	isNotNilOrEmpty,
	setTrackingId
} from "client/Misc/utils";
import {
	forwardRef,
	ReactElement,
	ReadonlyRefObject,
	Ref,
	useCallback,
	useImperativeHandle,
	useMemo,
	useRef
} from "react";
import { IconCard, IconCardHandle } from "./IconCard";
import styles from "./IconList.module.less";
import { Action } from "client_legacy/ndaw/actions";

export type IconListHandle = {
	scrollItemIntoView: (icon: AvailableIcon) => void;
};

export type IconListProps = {
	icons: readonly AvailableIcon[];
	selectedIcon: AvailableIcon | null;
	onSelectIcon: (icon: AvailableIcon) => void;
	actions: readonly Action<AvailableIcon>[] | undefined;
};

export const IconList = forwardRef(
	(props: IconListProps, ref: Ref<IconListHandle>): ReactElement => {
		const {
			icons,
			selectedIcon,
			onSelectIcon,
			actions
		} = props;

		const itemRefs: ReadonlyRefObject<Map<string, (IconCardHandle | null)>> = useRef<Map<string, (IconCardHandle | null)>>(new Map());

		const setItemRef = useCallback(
			(handle: IconCardHandle | null, icon: AvailableIcon): void => {
				itemRefs.current
					.set(
						getAppIconUniqueId(icon),
						handle
					);
			},
			[]
		);

		const iconGroups = useMemo(
			(): readonly IconGroup[] => groupIconsFromSortedIcons(icons),
			[icons]
		);

		useImperativeHandle(
			ref,
			(): IconListHandle => ({
				scrollItemIntoView: (icon) => {
					const targetRef: IconCardHandle | nil = itemRefs.current
						.get(getAppIconUniqueId(icon));

					targetRef?.scrollIntoView();
				}
			})
		);

		return (
			<ul {...setTrackingId("app-icon-list")}>
				{iconGroups.map(
					x => (
						<li key={x.key}>
							<Group
								setItemRef={setItemRef}
								icons={x.icons}
								title={x.title}
								selectedIcon={selectedIcon}
								onSelectIcon={onSelectIcon}
								actions={actions}
								trackingId={generateTrackingId("app-icon-group", `|${x.key}`)}
							/>
						</li>
					)
				)}
			</ul>
		);
	}
);
IconList.displayName = "IconList";

type GroupProps = {
	title: string;
	icons: readonly AvailableIcon[];
	selectedIcon: AvailableIcon | null;
	onSelectIcon: (icon: AvailableIcon) => void;
	actions: readonly Action<AvailableIcon>[] | undefined;
	setItemRef: (ref: IconCardHandle | null, icon: AvailableIcon) => void;
	trackingId: string;
};

const Group = (props: GroupProps): ReactElement => {
	const {
		icons,
		title,
		selectedIcon,
		actions,
		onSelectIcon,
		setItemRef,
		trackingId
	} = props;

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.group}
		>
			<h3 className={styles.groupTitle}>
				{title}
			</h3>
			<ul className={styles.iconList}>
				{icons.map(
					icon => {
						const uniqueId: string = getAppIconUniqueId(icon);
						return (
							<li key={uniqueId}>
								<IconCard
									ref={ref => setItemRef(ref, icon)}
									name={icon.name}
									icon={{
										imageUrl: icon.url,
										renderType: icon.renderType
									}}
									isSelected={selectedIcon
										? areTheSameAppIcons(icon, selectedIcon)
										: false
									}
									onSelect={() => onSelectIcon(icon)}
									trackingId={generateTrackingId("icon-card", `|${uniqueId}`)}
									actions={actions}
									actionContext={isNil(actions) ? undefined : icon}
								/>
							</li>
						);
					}
				)}
			</ul>
		</div>
	);
};
Group.DisplayName = "Group";

export function areTheSameAppIcons(
	a: AvailableIcon,
	b: AvailableIcon
): boolean {
	return a.id === b.id
		&& a.type === b.type;
}

function getAppIconUniqueId(
	icon: AvailableIcon
): string {
	return `${icon.id}|${icon.type}`;
}

type IconGroup = {
	key: string;
	title: string;
	icons: readonly AvailableIcon[];
};

function groupIconsFromSortedIcons(
	icons: readonly AvailableIcon[]
): readonly IconGroup[] {
	const result: IconGroup[] = [];
	let currentGroup: IconGroup | undefined;

	for (const icon of icons) {
		const newTitle: string = getTitleFromFirstLetter(icon.name);

		if (!currentGroup || newTitle !== currentGroup.title) {

			if (currentGroup) {
				result.push(currentGroup);
			}

			currentGroup = {
				key: getAppIconUniqueId(icon),
				icons: [icon],
				title: newTitle
			};
		} else {
			currentGroup.icons = [
				...currentGroup.icons,
				icon
			];
		}
	}

	if (currentGroup) {
		result.push(currentGroup);
	}

	return result;
}

function getTitleFromFirstLetter(name: string): string {
	if (isNotNilOrEmpty(name)) {
		const firstLetter: string = name[0];

		if (firstLetter.match(/^\d$/)) {
			return "#";
		}

		return firstLetter.toUpperCase();
	} else {
		return "*";
	}
}